import {Product, ProductType, School, Country, Currency, DiscountValueType, DiscountActionTargetTypeEnum, DiscountBandTypeEnum} from 'app/shared/model';
import {ActionType, Action, DateType, DateRangeField, PageLoadedData, AdditionalCriteriaType} from 'app/pages/DiscountEditor/actionTypes';
import { ProductOffering } from 'app/shared/model/ProductOffering';
import { AgentSearchResult, AgentOffice } from 'app/shared/model/Agent';
import { ProductFilterType, ProductFilter } from 'app/shared/model/ProductFilter';
import { Fee } from 'app/shared/model/Fee';
import { SavedDiscountBand, SavedDiscountAction, EditableBandedDiscount, DiscountTarget } from 'app/pages/DiscountEditor/modules/DiscountActions';
import { DiscountType } from 'app/shared/model/DiscountType';
import { DiscountDto } from 'app/shared/model/DiscountDto';
import { InvalidSaveDiscountResult, SuccessfulSaveDiscountResult } from 'app/shared/model/SaveDiscountResultDto';
import { CountrySelectionMode } from 'app/shared/model/CountrySelectionMode';
import { AgentSelectionMode } from 'app/shared/model/AgentSelectionMode';
import { SavedStudyStartDateCriteria } from 'app/pages/DiscountEditor/modules/AdditionalCriteria';
import { UnitsFilterType, UnitsFilter } from 'app/shared/model/ProductUnitFilter';
import { SavedBookingSystemCriteria } from 'app/pages/DiscountEditor/modules/AdditionalCriteria/state';

export function pageLoaded(discount: DiscountDto, data: PageLoadedData) : Action { 
    return {
        type: ActionType.PageLoaded,
        discount: discount,
        data: data
    };
}

export function feesLoaded(fees: Fee[]): Action {
    return { type: ActionType.FeesLoaded, fees };
}

export function catalogProductsLoaded(
    products: Product[],
    offerings?: ProductOffering[],
    loadedSchoolCodes?: string[]
) : Action {
    return { type: ActionType.CatalogProductsLoaded, products, offerings, loadedSchoolCodes };
}

export function offeringsLoadStarted(schoolIds: string[]) : Action {
    return {
        type: ActionType.OfferingsLoadStarted,
        schoolIds: schoolIds
    };
}

export function offeringsLoadError(error: string, schoolIds: string[]) : Action {
    return {
        type: ActionType.OfferingsLoadError,
        schoolIds: schoolIds,
        error: error
    };
}

export function offeringsInvalidateForSchools(schoolIds: string[]) : Action {
    return {
        type: ActionType.OfferingsInvalidateForSchools,
        schoolIds: schoolIds
    };
}

export function changeProductFilter(productFilter: ProductFilter) : Action {
    return {
        type: ActionType.ChangeProductFilter,
        productFilter: productFilter
    }
}

export function addOfferingsForSchool(offerings: ProductOffering[]) : Action {
    return {
        type: ActionType.AddOfferingsForSchool,
        offerings: offerings
    }
}

export function changeSchoolGroupOpenState(isOpen: boolean, code: string) : Action {
    return {
        type: ActionType.ChangeSchoolGroupOpenState,
        isOpen: isOpen,
        code: code
    }
}

export function changeSchoolGroupSelection(isSelected: boolean, code: string) : Action {
    return {
        type: ActionType.ChangeSchoolGroupSelection,
        isSelected: isSelected,
        code: code
    }
}

export function changeCentreGroupOpenState(isOpen: boolean, centreCode: string) : Action {
    return {
        type: ActionType.ChangeCentreGroupOpenState,
        isOpen: isOpen,
        centreCode: centreCode
    }
}

export function changeCentreGroupSelection(isSelected: boolean, centreCode: string) : Action {
    return {
        type: ActionType.ChangeCentreGroupSelection,
        isSelected: isSelected,
        centreCode: centreCode
    }
}

export function changeSchoolSelection(isSelected: boolean, code: string) : Action {
    return {
        type: ActionType.ChangeSchoolSelection,
        isSelected: isSelected,
        code: code
    }
}

export function changeUnitFilter(filter: UnitsFilter) : Action {
    return {
        type: ActionType.ChangeUnitFilter,
        filter: filter
    }
}

export function setNeverExpires(type: DateType, neverExpires: boolean) : Action {
    return {
        type: ActionType.DateNeverExpires, 
        dateType: type, 
        value: neverExpires
    }; 
}

export function setDate(type: DateType, field: DateRangeField, date: string) : Action {
    return {
        type: ActionType.SetDate, 
        dateType: type, 
        dateField: field,
        value: date 
    };
} 

export function changeInternalName(name: string) : Action {
    return {
        type: ActionType.ChangeInternalName, 
        value: name 
    };
} 

export function changePublicName(name: string) : Action {
    return {
        type: ActionType.ChangePublicName, 
        value: name 
    };
} 

export function changeDiscountType(type: DiscountType) : Action {
    return {
        type: ActionType.ChangeDiscountType,
        discountType: type
    }
}

export function changeDescription(description: string) : Action {
    return {
        type: ActionType.ChangeDescription, 
        value: description 
    };
}

export function changePromotionCode(code: string) : Action {
    return {
        type: ActionType.ChangePromotionCode,
        value: code
    };
}

export function changeAgentSelectionMode(mode: AgentSelectionMode) :  Action {
    return {
        type: ActionType.ChangeAgentSelectionMode,
        mode: mode
    };
}

export function agentSearchTermChanged(searchTerm: string) :  Action {
    return {
        type: ActionType.AgentSearchChanged,
        searchTerm: searchTerm
    };
}

export function agentSearchResultsLoaded(searchTerm: string, results: AgentSearchResult[]) : Action {
    return {
        type: ActionType.AgentSearchResultsLoaded,
        results: results,
        searchTerm: searchTerm
    };
}

export function agentSearchFailed() : Action {
    return {
        type: ActionType.AgentSearchFailed
    };
}

export function agentSearchResultSelected(agent: AgentSearchResult) : Action {
    return{
        type: ActionType.AgentSearchResultSelected,
        agent: agent
    };
}

export function changeAgentSelection(id: string, isSelected: boolean) : Action {
    return {
        type: ActionType.ChangeAgentSelection,
        id: id,
        isSelected: isSelected
    };
}

export function agentOfficesLoaded(agentId: string, offices: AgentOffice[], isSelected: boolean) : Action {
    return {
        type: ActionType.AgentOfficesLoaded,
        agentId: agentId,
        offices: offices,
        isSelected: isSelected
    };
}

export function agentOfficeLoadFailed(agentId: string) : Action {
    return {
        type: ActionType.AgentOfficesLoadFailed,
        agentId: agentId,
    };
}

export function showOrHideAgentOffices(agentId: string, show: boolean) : Action {
    return {
        type: ActionType.ShowOrHideAgentOffices,
        agentId: agentId,
        show: show
    };
}

export function changeAgentOfficeSelection(agentId: string, officeId: string, isSelected: boolean, isAgentSelected: boolean|undefined) : Action {
    return {
        type: ActionType.ChangeAgentOfficeSelection,
        agentId: agentId,
        officeId: officeId,
        isSelected: isSelected,
        isAgentSelected: isAgentSelected
    };
}

export function addCountrySelection(countryId: string) : Action {
    return {
        type: ActionType.AddCountrySelection,
        countryId: countryId
    }
}

export function changeCountrySelectionMode(countrySelectionMode: CountrySelectionMode) : Action {
    return {
        type: ActionType.ChangeCountrySelectionMode,
        mode: countrySelectionMode
    };
}

export function changeCountrySelection(countryId: string, isSelected: boolean) : Action {
    return {
        type: ActionType.ChangeCountrySelection,
        countryId: countryId,
        isSelected: isSelected
    };
}

export function startAddingDiscountAction(
    target: DiscountActionTargetTypeEnum,
    bandType: DiscountBandTypeEnum,
    initialTarget?: DiscountTarget
) : Action {
    return {
        type: ActionType.StartAddingDiscountAction, 
        target: target, 
        bandType: bandType,
        initialTarget
    }
}

export function startEditingDiscountAction(action: SavedDiscountAction) : Action {
    return {
        type: ActionType.StartEditingDiscountAction, 
        discountAction: action
    };
}

export function showAddDiscountActionWizard() : Action {
    return {
        type: ActionType.ShowAddDiscountActionWizard
    };
}

export function showDeleteActionConfirmation(action: SavedDiscountAction) : Action {
    return {
        type: ActionType.ShowDeleteDiscountActionConfirmation, 
        discountAction: action
    }; 
}

export function cancelShowDeleteActionConfirmation() : Action {
    return {
        type: ActionType.CancelShowDeleteDiscountActionConfirmation
    };
}

export function cancelAddOrUpdateDiscountAction() : Action {
    return {
        type: ActionType.CancelAddOrUpdateDiscountAction
    };
}

export function updateEditableBadedDiscount(bandedDiscount: EditableBandedDiscount) : Action {
    return {
        type: ActionType.UpdateEditableDiscountAction,
        discountAction: bandedDiscount
    }
}

export function changeDiscountActionValueType(valueType: DiscountValueType) : Action {
    return {
        type: ActionType.ChangeDiscountActionValueType,
        valueType: valueType
    };
}

export function saveDiscountAction(discountAction: EditableBandedDiscount) : Action {
    return {
        type: ActionType.SaveDiscountAction,
        discountAction: discountAction
    };
}

export function deleteDiscountAction(id: string) : Action {
    return {
        type: ActionType.DeleteDiscountAction, 
        id: id
    }; 
}

export function saveStarted(isNew: boolean) : Action {
    return {
        type: ActionType.SaveStarted,
        isNew: isNew
    }
}

export function saveComplete(data: SuccessfulSaveDiscountResult, isNew: boolean) : Action {
    return {
        type: ActionType.SaveSuccessful,
        isNew: isNew,
        result: data
    }
}

export function saveInvalid(data: InvalidSaveDiscountResult) : Action {
    return {
        type: ActionType.SaveInvalid,
        result: data
    }
}

export function saveError() : Action {
    return {
        type: ActionType.SaveFailed
    }
}

export function setAddOrEditCriteriaModal(type: AdditionalCriteriaType) : Action {
    return {
        type: ActionType.SetAddOrEditCriteriaModal,
        modal: type
    }
}

export function saveStudentStartDateCriteria(criteria: SavedStudyStartDateCriteria) : Action {
    return {
        type: ActionType.SaveStudentStartDateCriteria, 
        criteria: criteria
    }
}

export function setAdditionalCriteraProductFilter(productFilter: ProductFilter, unitsFilter: UnitsFilter) : Action {
    return {
        type: ActionType.SetAdditionalCriteriaProductFilter,
        productFilter: productFilter,
        unitsFilter: unitsFilter
    }
}

export function saveBookingSystemCriteria(criteria: SavedBookingSystemCriteria) : Action {
    return {
        type: ActionType.SaveBookingSystemsCriteria, 
        criteria: criteria
    }
}

export function deleteAdditionalCriteria(type: AdditionalCriteriaType) : Action {
    return {
        type: ActionType.DeleteAdditionalCriteria, 
        criteriaType: type
    }
}

export function setDeleteCriteriaConfirmationModal(type: AdditionalCriteriaType) : Action {
    return {
        type: ActionType.SetDeleteCriteriaConfirmationModal, 
        criteriaType: type
    }
}